import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { SMLRouteNames } from './SMLRoutes';

/** The query of an old Insights link that started a connect in the app. */
const LEGACY_CONNECT_PARAM = 'connect';

/** The service names of the old `?connect=` links, and the name of the new path. */
const LEGACY_CONNECT_SERVICES: Record<string, string> = {
  'spotify-for-artists': 'spotify-artists',
};

/**
 * Where an old `/insights` link goes (design your-intelligence h4). `?connect=<service>` opens the
 * connect of that service in Catalog; every other link opens Streaming. The other query values
 * stay.
 */
export function insightsRedirectTarget(search: string): string {
  const params = new URLSearchParams(search);
  const connect = params.get(LEGACY_CONNECT_PARAM);
  if (connect !== null && connect !== '') {
    params.delete(LEGACY_CONNECT_PARAM);
    const rest = params.toString();
    const service = LEGACY_CONNECT_SERVICES[connect] ?? connect;
    return `/${SMLRouteNames.catalog}/connect/${encodeURIComponent(service)}${rest === '' ? '' : `?${rest}`}`;
  }
  return `/${SMLRouteNames.streaming}${search}`;
}

/** `/insights` → `/streaming`, or the Catalog connect of an old `?connect=` link. */
export const InsightsRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={insightsRedirectTarget(search)} replace />;
};

/** `/insights/connect/:service` → `/catalog/connect/:service`, with the query. */
export const InsightsConnectRedirect: React.FC = () => {
  const { search } = useLocation();
  const { service = '' } = useParams<{ service: string }>();
  const name = LEGACY_CONNECT_SERVICES[service] ?? service;
  return <Navigate to={`/${SMLRouteNames.catalog}/connect/${encodeURIComponent(name)}${search}`} replace />;
};

/** The query names under which an old Performance link can carry the id of a social post. */
const LEGACY_POST_PARAMS = ['postId', 'post', 'socialPostId'];

/**
 * Where an old `/promote/promotePerformance` link goes. A link with the id of a social post
 * (`?postId=12`, `?post=12` or `?socialPostId=12`) opens that post: `/social/post/12`. Every other
 * link opens `/social`. The other query values stay.
 */
export function performanceRedirectTarget(search: string): string {
  const params = new URLSearchParams(search);
  const name = LEGACY_POST_PARAMS.find((param) => /^\d+$/.test(params.get(param) ?? ''));
  if (name === undefined) {
    return `/${SMLRouteNames.social}${search}`;
  }
  const postId = params.get(name) as string;
  for (const param of LEGACY_POST_PARAMS) {
    params.delete(param);
  }
  const rest = params.toString();
  return `/${SMLRouteNames.social}/post/${postId}${rest === '' ? '' : `?${rest}`}`;
}

/** `/promote/promotePerformance` → `/social` (or `/social/post/:id`), with the query. */
export const PerformanceRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={performanceRedirectTarget(search)} replace />;
};
